import { useEffect, useRef, useState } from 'react';
import {
  PRESETS,
  PRESET_BY_KEY,
  dateInputToRange,
} from '../../utils/analytics/dateRange';
import type { DateRange, PresetKey } from '../../utils/analytics/dateRange';

interface DateSelectorProps {
  value: DateRange;
  /** Active preset (null = Custom range is in effect). Parent owns this so it can
   * recompute relative presets when the user clicks Load. */
  presetKey: PresetKey | null;
  onChange: (range: DateRange, presetKey: PresetKey | null) => void;
}

function isoUtcToLocalYmd(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function DateSelector({ value, presetKey, onChange }: DateSelectorProps) {
  const activePreset = presetKey;
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => isoUtcToLocalYmd(value.from));
  const [customTo, setCustomTo] = useState(() => isoUtcToLocalYmd(value.to));
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!customOpen) return;
    function onDocClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setCustomOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [customOpen]);

  function applyPreset(key: PresetKey) {
    const range = PRESET_BY_KEY[key].compute();
    setCustomOpen(false);
    setCustomFrom(isoUtcToLocalYmd(range.from));
    setCustomTo(isoUtcToLocalYmd(range.to));
    onChange(range, key);
  }

  function applyCustom() {
    if (!customFrom || !customTo) return;
    if (customFrom > customTo) return;
    const range = dateInputToRange(customFrom, customTo);
    onChange(range, null);
    setCustomOpen(false);
  }

  const customLabel =
    customFrom && customTo
      ? customFrom === customTo
        ? customFrom
        : `${customFrom} → ${customTo}`
      : 'Custom…';

  return (
    <div className="flex flex-wrap items-center gap-1 relative">
      <label className="text-sm font-medium text-gray-600 mr-1">Range</label>
      {PRESETS.map((p) => (
        <button
          key={p.key}
          onClick={() => applyPreset(p.key)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            activePreset === p.key
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {p.label}
        </button>
      ))}
      <button
        onClick={() => setCustomOpen((v) => !v)}
        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          activePreset === null
            ? 'bg-blue-600 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {activePreset === null ? customLabel : 'Custom…'}
      </button>

      {customOpen && (
        <div
          ref={popoverRef}
          className="absolute top-full mt-2 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10 w-80 max-w-[calc(100vw-2rem)]"
        >
          <p className="text-xs font-semibold text-gray-700 mb-2">Custom range</p>
          <div className="flex flex-col gap-2">
            <label className="flex items-center justify-between gap-2 text-sm">
              <span className="text-gray-600">From</span>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <label className="flex items-center justify-between gap-2 text-sm">
              <span className="text-gray-600">To</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <p className="text-[11px] text-gray-400 mt-1">
              Inclusive on both ends · interpreted in your local timezone.
            </p>
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setCustomOpen(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={applyCustom}
                disabled={!customFrom || !customTo || customFrom > customTo}
                className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
